import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { MessageSquare, Bot, User, Search, RefreshCw, Phone, CheckCircle, XCircle } from 'lucide-react';
import { GlassCard } from '../components/ui/GlassCard';
import { StatusBadge } from '../components/ui/StatusBadge';
import { AgentAvatar } from '../components/voice/AgentAvatar';
import { agentKeyFromType } from '../lib/agents';
import { isDemo } from '../lib/auth';
import { pageTransition, fadeUpItem, staggerContainer } from '../hooks/useAnimation';

const API = '/api/agents';

interface CallSummary {
  call_id: string;
  from_number: string;
  customer_name: string | null;
  account_number: string | null;
  verified: boolean;
  status: string;
  intent: string | null;
  tokens: number;
  turn_count: number;
  recording_path?: string | null;
  started_at: string | null;
  ended_at: string | null;
}

interface Turn { speaker: 'caller' | 'agent'; agent?: string; text: string; tokens?: number; }
interface CallDetail extends CallSummary { turns: Turn[]; }

function fmtTime(iso: string | null): string {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }); }
  catch { return iso; }
}

export function Transcripts() {
  const [calls, setCalls] = useState<CallSummary[]>([]);
  const [active, setActive] = useState<CallDetail | null>(null);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [recUrl, setRecUrl] = useState<string | null>(null);

  // Fetch the recording with the auth interceptor, then play it from a blob URL
  // (a native <audio src> request would bypass the JWT and get a 401).
  useEffect(() => {
    setRecUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
    if (!active?.recording_path) return;
    let revoked = false;
    let url = '';
    (async () => {
      try {
        const r = await fetch(`${API}/calls/${encodeURIComponent(active.call_id)}/recording`);
        if (!r.ok) return;
        url = URL.createObjectURL(await r.blob());
        if (!revoked) setRecUrl(url);
      } catch { /* ignore */ }
    })();
    return () => { revoked = true; if (url) URL.revokeObjectURL(url); };
  }, [active?.call_id, active?.recording_path]);

  const loadCalls = async () => {
    setLoading(true); setError('');
    try {
      const r = await fetch(`${API}/calls?limit=100`);
      const d = await r.json();
      let list: CallSummary[] = d.calls || [];
      // Demo account: show only real calls placed from the Voice Call page
      // (id "WEBCALL-…"), hiding scripted/simulated/test calls.
      if (isDemo()) list = list.filter((c) => (c.call_id || '').startsWith('WEBCALL-'));
      setCalls(list);
      if (list.length && !active) selectCall(list[0].call_id);
    } catch { setError('Could not load call history'); }
    finally { setLoading(false); }
  };

  const selectCall = async (id: string) => {
    try {
      const r = await fetch(`${API}/calls/${encodeURIComponent(id)}`);
      if (!r.ok) return;
      setActive(await r.json());
    } catch { /* ignore */ }
  };

  useEffect(() => { loadCalls(); /* eslint-disable-next-line */ }, []);

  const list = calls.filter((c) =>
    (c.customer_name || '').toLowerCase().includes(query.toLowerCase()) ||
    c.call_id.toLowerCase().includes(query.toLowerCase()) ||
    (c.from_number || '').includes(query),
  );

  return (
    <motion.div variants={pageTransition} initial="initial" animate="animate" exit="exit" className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-text-primary flex items-center gap-2">
            <MessageSquare className="w-6 h-6 text-vokentra" /> Transcripts
          </h1>
          <p className="text-sm text-text-muted mt-1">Completed calls and full conversations (admin review)</p>
        </div>
        <button onClick={loadCalls} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.06] text-text-secondary hover:text-text-primary">
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {error && <p className="text-sm text-error">{error}</p>}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* List */}
        <div className="lg:col-span-1 space-y-3">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-white/[0.03] border border-white/[0.06]">
            <Search className="w-4 h-4 text-text-muted" />
            <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search by name, number, ID…"
              className="bg-transparent text-sm text-text-primary placeholder:text-text-muted outline-none w-full" />
          </div>

          {calls.length === 0 && !loading && (
            <GlassCard padding="md"><p className="text-sm text-text-muted text-center py-6">No calls yet. Make a call on the Voice Call page, then end it to see it here.</p></GlassCard>
          )}

          <motion.div variants={staggerContainer(0.05)} initial="hidden" animate="visible" className="space-y-2 max-h-[60vh] overflow-y-auto">
            {list.map((c) => (
              <motion.button key={c.call_id} variants={fadeUpItem} onClick={() => selectCall(c.call_id)}
                className={`w-full text-left rounded-xl p-3 border transition-colors ${active?.call_id === c.call_id ? 'bg-vokentra/10 border-vokentra/30' : 'bg-white/[0.02] border-white/[0.06] hover:bg-white/[0.04]'}`}>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-text-primary truncate">{c.customer_name || 'Unknown caller'}</span>
                  <StatusBadge status={c.status === 'completed' ? 'ended' : c.status === 'failed' ? 'error' : 'active'} size="sm" />
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[11px] text-text-muted truncate flex items-center gap-1">
                    <Phone className="w-3 h-3" /> {c.from_number}
                  </span>
                  <span className="text-[10px] text-text-muted">{fmtTime(c.started_at)}</span>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span className="text-[11px] text-text-muted truncate">{c.intent || '—'}</span>
                  <span className="text-[10px] text-info tabular-nums">{c.tokens} tok · {c.turn_count} turns</span>
                </div>
              </motion.button>
            ))}
          </motion.div>
        </div>

        {/* Detail */}
        <div className="lg:col-span-2">
          <GlassCard padding="md" className="h-full">
            {!active ? (
              <p className="text-sm text-text-muted text-center py-10">Select a call to view its conversation.</p>
            ) : (
              <>
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                  <div>
                    <h3 className="text-text-primary font-semibold flex items-center gap-2">
                      {active.customer_name || 'Unknown caller'}
                      {active.verified ? <CheckCircle className="w-4 h-4 text-success" /> : <XCircle className="w-4 h-4 text-error" />}
                    </h3>
                    <p className="text-[11px] text-text-muted">
                      {active.call_id} · {active.from_number} · {active.account_number || 'unverified'} · {fmtTime(active.started_at)}
                    </p>
                  </div>
                  <div className="text-right">
                    <StatusBadge status={active.status === 'completed' ? 'ended' : active.status === 'failed' ? 'error' : 'active'} size="sm" />
                    <p className="text-[11px] text-info mt-1">{active.tokens} tokens</p>
                  </div>
                </div>
                {active.recording_path && (
                  <div className="mb-4 flex items-center gap-2">
                    <span className="text-[11px] text-text-muted">Voice recording (WAV):</span>
                    {recUrl
                      ? <audio controls src={recUrl} className="h-8" />
                      : <span className="text-[11px] text-text-muted">loading…</span>}
                  </div>
                )}
                <div className="space-y-3 max-h-[52vh] overflow-y-auto">
                  {active.turns.map((t, i) => (
                    <div key={i} className={`rounded-xl p-3 ${t.speaker === 'caller' ? 'bg-white/[0.04]' : 'bg-vokentra/10'}`}>
                      <div className="flex items-center gap-1.5 text-[11px] text-text-muted mb-1">
                        {t.speaker === 'caller' ? <User className="w-3.5 h-3.5" /> : <AgentAvatar agent={agentKeyFromType(t.agent || 'voice')} size={16} />}
                        <span className="font-medium">{t.speaker === 'caller' ? 'Caller' : 'Agent'}</span>
                        {t.tokens ? <span className="ml-1 text-info">· {t.tokens} tokens</span> : null}
                      </div>
                      <p className="text-sm text-text-primary leading-relaxed">{t.text}</p>
                    </div>
                  ))}
                  {active.turns.length === 0 && <p className="text-sm text-text-muted">No conversation recorded.</p>}
                </div>
              </>
            )}
          </GlassCard>
        </div>
      </div>
    </motion.div>
  );
}

export default Transcripts;
